// Instalação do SIAC no computador ou celular (PWA). O navegador (Chrome/Edge) avisa
// que o sistema pode ser instalado disparando `beforeinstallprompt` — uma
// única vez, logo que a página carrega, então este módulo é importado no
// index.tsx, antes de qualquer tela montar, pra não perder o evento. O botão
// "Instalar SIAC" (ver MenuLateral.tsx) só aparece enquanto ele estiver
// guardado. Instalado, o sistema abre numa janela própria e, sem internet,
// carrega pela cópia guardada pelo service-worker.ts.

interface IEventoInstalacao extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let eventoGuardado: IEventoInstalacao | null = null;
const ouvintes = new Set<() => void>();
const avisar = () => ouvintes.forEach((ouvinte) => ouvinte());

window.addEventListener('beforeinstallprompt', (evento) => {
  // Impede a barrinha automática do navegador — a instalação é oferecida
  // pelo botão do sistema.
  evento.preventDefault();
  eventoGuardado = evento as IEventoInstalacao;
  avisar();
});

window.addEventListener('appinstalled', () => {
  eventoGuardado = null;
  avisar();
});

export const podeInstalar = () => eventoGuardado !== null;

// Rodando como app instalado (janela própria), não numa aba do navegador.
export const rodandoInstalado = () =>
  window.matchMedia?.('(display-mode: standalone)').matches
  || (navigator as Navigator & { standalone?: boolean }).standalone === true;

// iPhone/iPad: o Safari não dispara `beforeinstallprompt` — a Apple só deixa
// instalar pelo botão Compartilhar → "Adicionar à Tela de Início". Então lá
// o item de instalar abre um passo a passo (DialogInstalarIos.tsx) em vez de
// chamar o instalador. iPadOS se apresenta como "Macintosh" — o toque
// (maxTouchPoints) é o que diferencia de um Mac de verdade.
export const ehIos = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent)
  || (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1);

// No iPhone só o Safari instala (Chrome/Firefox/Edge do iOS não têm a opção).
export const ehSafariIos = () => ehIos() && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(navigator.userAgent);

// Como oferecer a instalação neste aparelho: 'automatica' = instalador do
// navegador (Android/Chrome/Edge no computador), 'ios' = passo a passo do
// Safari, null = já instalado ou o navegador não permite.
export const formaInstalacao = (): 'automatica' | 'ios' | null => {
  if (rodandoInstalado()) return null;
  if (podeInstalar()) return 'automatica';
  if (ehIos()) return 'ios';
  return null;
};

export const inscreverInstalacao = (ouvinte: () => void) => {
  ouvintes.add(ouvinte);
  return () => { ouvintes.delete(ouvinte); };
};

// Abre a janela de confirmação do navegador. O evento só pode ser usado uma
// vez — aceito ou recusado, o botão some até o navegador oferecer de novo.
export const instalarApp = async (): Promise<boolean> => {
  const evento = eventoGuardado;
  if (!evento) return false;
  eventoGuardado = null;
  avisar();
  await evento.prompt();
  const { outcome } = await evento.userChoice;
  return outcome === 'accepted';
};
